import { faChalkboardTeacher, faChevronDown, faChevronUp, faGlobe } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  BottomNavigation,
  Box,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Switch,
  useTheme
} from "@mui/material"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { hasRole } from "auth/hasRole"
import { useApiFile } from "auth/useApi2"
import { AppContext } from "components/AppContextProvider"
import Button from "components/Button"
import FilePicker from "components/FilePicker"
import PaperBox from "components/PaperBox"
import { CircleIcon } from "components/icons/CircleIcon"
import { FieldArray, Formik } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import { t as globalT } from "i18next"
import { useContext, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ModuleBox } from "./ModuleBox"
import { customerValidationSchema } from "./validationSchema"
import { useApi } from "auth/useApi3"

export const CustomerForm = ({
  editCustomer,
  saveResult,
  feedback,
  handleSubmit,
  logoResult,
  customerResult
}) => {
  const appContext = useContext(AppContext)
  const formRef = useRef()
  const { t } = useTranslation()
  const [fileUploadResult, uploadFile] = useApiFile()
  const [file, setFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState()
  const [languages, setLanguages] = useState([])
  const [formTemplateResult, formTemplateApi] = useApi()
  const theme = useTheme()

  useEffect(() => {
    formTemplateApi.get(apiEndpoints.form.getformtemplateminimized.replace("{feature}", "deviation"))
  }, [])

  useEffect(() => {
    const availableLanguages = appContext.availableLanguages || {}
    setLanguages(availableLanguages)
  }, [editCustomer, appContext.availableLanguages])

  useEffect(() => {
    if (logoResult.status === 2) {
      onLogoChanged(logoResult.data)
    }
  }, [logoResult])

  useEffect(() => {
    const onAddImage = async () => {
      await uploadFile(apiEndpoints.systemadmincustomers.uploadcustomerlogo, file)
    }

    if (file) {
      onAddImage()
    }
  }, [file, uploadFile])

  const onLogoChanged = (value) => {
    setLogoPreview(value)
  }

  const initialValues = editCustomer ? editCustomer : customerResult

  const [systemAdminModules, toggleSystemAdminModules] = useState(false)

  return (
    <>
      <Formik
        innerRef={formRef}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={customerValidationSchema(t)}
        validateOnChange={false}>
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3} direction="row">
              <Grid item xs={12} lg={7}>
                <PaperBox mt={0}>
                  <Grid container direction="row" spacing={2}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        autoComplete="off"
                        fullWidth
                        id="name"
                        label={t("settings.form.name", "Name")}
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && errors.name ? true : false}
                        helperText={touched.name && errors.name ? errors.name : null}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        autoComplete="off"
                        fullWidth
                        id="organisationNumber"
                        label={t("settings.form.orgnr", "Corporate identity nr")}
                        value={values.organisationNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.organisationNumber && errors.organisationNumber ? true : false}
                        helperText={
                          touched.organisationNumber && errors.organisationNumber
                            ? errors.organisationNumber
                            : null
                        }
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        autoComplete="off"
                        fullWidth
                        id="streetAddress"
                        label={t("settings.form.streetAddress", "Street address")}
                        value={values.streetAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.streetAddress && errors.streetAddress ? true : false}
                        helperText={
                          touched.streetAddress && errors.streetAddress ? errors.streetAddress : null
                        }
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        autoComplete="off"
                        fullWidth
                        id="postalCode"
                        label={t("settings.form.postalCode", "Postal code")}
                        value={values.postalCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.postalCode && errors.postalCode ? true : false}
                        helperText={touched.postalCode && errors.postalCode ? errors.postalCode : null}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <TextField
                        autoComplete="off"
                        fullWidth
                        id="cityAddress"
                        label={t("settings.form.city", "City")}
                        value={values.cityAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.cityAddress && errors.cityAddress ? true : false}
                        helperText={touched.cityAddress && errors.cityAddress ? errors.cityAddress : null}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={8} marginTop={3}>
                    <FilePicker
                      setFieldValue={setFieldValue}
                      fieldName="logoName"
                      defaultPreviewImage={
                        logoPreview && `data:${logoPreview.contenType};base64,${logoPreview.base64Contents}`
                      }
                      setFile={setFile}
                      fileUploadResult={fileUploadResult}
                      onLogoChanged={onLogoChanged}
                      label={t("global.logo", "Logo")}
                    />
                  </Grid>
                </PaperBox>
                <Grid container>
                  <Grid item xs={12}>
                    <FieldArray
                      name="modules"
                      render={() => (
                        <div>
                          {values?.modules &&
                            values.modules?.length > 0 &&
                            values.modules.map((module, index) => (
                              <ModuleBox
                                index={index}
                                module={module}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                menuIcon={module.icon}
                                setFieldValue={setFieldValue}
                                arrayName="modules"
                                modules={values.modules}
                                touched={touched}
                                errors={errors}
                                formTemplateResult={formTemplateResult}
                              />
                            ))}
                        </div>
                      )}
                    />
                    {hasRole(appContext.appUser, allRoles.SystemAdminCustomers) && (
                      <>
                        <Typography
                          marginTop={3}
                          variant="h5"
                          sx={{ cursor: "pointer" }}
                          onClick={() => toggleSystemAdminModules(!systemAdminModules)}>
                          {t("settings.form.systemadmin", "Customer admin")}{" "}
                          <FontAwesomeIcon icon={systemAdminModules ? faChevronUp : faChevronDown} />
                        </Typography>

                        <Typography variant="bodySmall">
                          {t(
                            "settings.form.systemadmin_description",
                            "The modules provide access to all or several customers data"
                          )}
                        </Typography>
                        {systemAdminModules && (
                          <FieldArray
                            name="systemModules"
                            render={() => (
                              <div>
                                {values.systemModules &&
                                  values.systemModules.length > 0 &&
                                  values.systemModules.map((module, index) => (
                                    <ModuleBox
                                      index={index}
                                      module={module}
                                      handleChange={handleChange}
                                      handleBlur={handleBlur}
                                      menuIcon={module.icon}
                                      setFieldValue={setFieldValue}
                                      arrayName="systemModules"
                                      values={values}
                                      modules={values.systemModules}
                                      touched={touched}
                                      errors={errors}
                                      formTemplateResult={formTemplateResult}
                                    />
                                  ))}
                              </div>
                            )}
                          />
                        )}
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid xs={12} lg={5} pl={2} pt={{ xs: 2, lg: 1 }}>
                <PaperBox marginBottom={2}>
                  <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Grid item>
                      <CircleIcon
                        icon={faGlobe}
                        backgroundColor={theme.palette.primary[300]}
                        color={theme.palette.primary[900]}
                        size="lg"
                      />
                    </Grid>

                    <Grid item xs={8}>
                      <Typography variant="h5">
                        {t("settings.form.default_language", "Default language")}
                      </Typography>
                      <Typography variant="bodySmall">
                        {t(
                          "settings.form.defaukt_language_description",
                          "Choose which language to use as the default in the system"
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} xl={2} mt={{ xs: 2, xl: 0 }}>
                      <Select
                        fullWidth
                        id="defaultLanguage"
                        name="defaultLanguage"
                        value={values.defaultLanguage || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="outlined">
                        {languages &&
                          Object.keys(languages).map((lng) => {
                            return (
                              <MenuItem key={lng} value={lng}>
                                {languages[lng].name || lng.toUpperCase()}
                              </MenuItem>
                            )
                          })}
                      </Select>
                    </Grid>
                  </Grid>
                </PaperBox>
                <PaperBox marginBottom={2}>
                  <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Grid item>
                      <CircleIcon
                        icon={faChalkboardTeacher}
                        backgroundColor={theme.palette.primary[300]}
                        color={theme.palette.primary[900]}
                        size="lg"
                      />
                    </Grid>

                    <Grid item xs={8}>
                      <Typography variant="h5">{t("settings.form.demo_account", "Demo account")}</Typography>
                      <Typography variant="bodySmall">
                        {t("settings.form.demo_account_description")}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Switch checked={values.isDemoAccount} onChange={handleChange} id="isDemoAccount" />
                    </Grid>
                  </Grid>
                </PaperBox>

                {values.modules?.find((item) => item.feature === "VehicleRegistry")?.enabled &&
                  values.modules
                    .find((item) => item.feature === "VehicleRegistry")
                    ?.features?.some((feature) => feature.feature === "IssueReports" && feature.enabled) && (
                    <PaperBox>
                      <Grid
                        container
                        direction="row"
                        spacing={2}
                        justifyContent="space-between"
                        alignItems="center">
                        <Grid item xs={12}>
                          <Typography variant="h5">{t("global.issuereports", "Issue reports")}</Typography>
                          <Typography variant="bodyMedium">
                            {t("settings.form.settings_for_issue_reports", "Error reporting settings")}
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body">
                            {t(
                              "settings.form.disable_possibility_to_delete_issue_reports",
                              "Disable ability to delete error reports"
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.issueReportDeleteDisabled}
                                onChange={handleChange}
                                id="issueReportDeleteDisabled"
                              />
                            }
                          />
                        </Grid>
                      </Grid>
                    </PaperBox>
                  )}
              </Grid>
            </Grid>

            <Box marginTop={15}></Box>
            <Box sx={{ position: "fixed", bottom: 8, left: 0, right: 15 }} elevation={3}>
              <BottomNavigation
                sx={{ justifyContent: "flex-end", backgroundColor: "transparent", height: "auto" }}>
                <Button type="submit" color="primary" result={saveResult} feedback={feedback}>
                  {globalT("global.buttons.save")}
                </Button>
              </BottomNavigation>
            </Box>
          </form>
        )}
      </Formik>
    </>
  )
}
